import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { hasFeature, effectivePlan } from '@/lib/entitlements';
import { UpgradeGate } from '@/components/UpgradePrompt';
import { Bar } from '@/components/common/SectionLoading';

/**
 * GatedRoute, wraps a page; if the user's effective plan (incl. trial) lacks
 * `featureKey`, renders a calm UpgradeGate instead of the page.
 *
 * Phase 84.23.12 (2026-07-29) — production race fix: AuthContext sets
 * `loading=false` as soon as the auth SESSION resolves, but the `profile` row is
 * fetched separately and may still be null for one or two ticks. In that window
 * `effectivePlan(null) === 'free'`, which fails every non-Clarity feature check
 * and briefly renders "Available in Horizon" for Summit-beta users on hard
 * refresh.
 *
 * Phase 86.J (2026-08-15) — that fix returned `null`, which turned a slow or
 * failed profile read into a permanently BLANK page: the shell rendered, the
 * route rendered nothing, and the user saw an empty canvas after a refresh
 * (reported on Safari). Two changes:
 *   * while the profile is hydrating we render a page-shaped placeholder, not
 *     nothing, so the wait is visible and the layout is already reserved;
 *   * if the profile still hasn't landed after 4s we render the page anyway.
 *     Entitlement display is not a security boundary — the backend enforces
 *     plan limits — so showing the page beats showing a void.
 */
const PROFILE_GRACE_MS = 4000;

function PagePlaceholder() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading your page"
         className="space-y-6" data-testid="route-loading">
      <div>
        <Bar className="h-7 w-64" />
        <Bar className="h-3 w-80 mt-3" />
      </div>
      <Bar className="h-10 w-full max-w-md" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-card px-4 py-3.5">
            <Bar className="h-2 w-20" />
            <Bar className="h-5 w-24 mt-2.5" />
          </div>
        ))}
      </div>
      <Bar className="h-48 w-full rounded-2xl" />
      <span className="sr-only">Loading your page</span>
    </div>
  );
}

export default function GatedRoute({ featureKey, children }) {
  const { user, profile, loading } = useAuth();
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    if (profile || !user) return undefined;
    const t = setTimeout(() => setWaited(true), PROFILE_GRACE_MS);
    return () => clearTimeout(t);
  }, [user, profile]);

  if (loading) return <PagePlaceholder />;
  if (user && !profile && !waited) return <PagePlaceholder />;
  if (user && !profile && waited) {
    // The profile read is slow or failed. Show the page rather than a void.
    console.warn('[gate] profile never arrived; rendering page ungated');
    return children;
  }
  const plan = effectivePlan(profile);
  if (hasFeature(plan, featureKey)) return children;
  return <UpgradeGate featureKey={featureKey} />;
}
